import React from "react";
import cn from "classnames";
import "./index.css";
import BgTransformer from "../../../layout/BgTransformer";
import ContentEditable from "../../../layout/ContentEditable/ContentEditable";
import Textarea from "../../../layout/Textarea/Textarea";
import ImageUpload from "../../../layout/ImageUpload";
import PixelationLayer from "../../../layout/PixelationLayer";
import {useSetting} from "../../../contexts/setting";
import {useEdits} from "../../../contexts/edits";
import {useBgUrl} from "../../../hooks/useBgUrl";
import {useLogoUrl} from "../../../hooks/useLogoUrl";
import {toModulCase} from "../../../libs/modulCase";
import {versionLineCount} from "./versionLines";
import {AIR_MARK, POWERED_WITH, attributionPart, type TextField} from "./airAttribution";

type SplashTexts = Record<"productNames" | "subtitles" | "subtitles2" | "badges", TextField>;

interface SplashLayout2026Props {
  id: string;
  zoom: number;
  /** One design pixel in CSS pixels: 1 on the 640x400 canvas, 2 on the @2x canvas. */
  scale: 1 | 2;
  /** The 2025 design of the same canvas. Its crops position the background until this design has its own. */
  fallbackTransformId: string;
}

/**
 * The 2026 splash design, from 2026.3 (Figma: "Splashes / Release Graphics
 * System", frame 277:1785 "Splash — IntelliJ IDEA — layout 1"): the product
 * name and the version on the left, the "powered with air" line in the
 * bottom-left corner, the logo in the bottom-right corner. Splash2026 and
 * Splash2x2026 draw it at scale 1 and 2, as the frame's 1x and @2x exports.
 *
 * Fields: the product name is `productNames`, the version is `subtitles` (the
 * 2025 design keeps the version there too), "powered with" is `subtitles2`,
 * and the Air mark "air" is `badges`. The mark uses the JetBrains Modul face
 * (src/css/fonts.css), so it stays editable text.
 */
const SplashLayout2026 = ({id, zoom, scale, fallbackTransformId}: SplashLayout2026Props) => {
  const {product, layoutOption} = useSetting();
  const {url: bgUrl} = useBgUrl();
  const {productNames, subtitles, subtitles2, badges}: SplashTexts = useEdits();
  const logoUrl = useLogoUrl(id);

  const productName = productNames[id] || productNames.main || product.altLabel || product.label;
  const version = subtitles[id] ?? subtitles.main ?? "";
  const poweredWith = attributionPart(subtitles2, id, POWERED_WITH);
  const airMark = attributionPart(badges, id, AIR_MARK);

  const style = {
    "--zoom-level": zoom,
    "--u": `${scale}px`,
    "--version-lines": versionLineCount(version),
  } as React.CSSProperties;

  return (
    <div
      id={id}
      className={cn("template", `layout-${layoutOption}`, product.value)}
      style={style}
    >
      <div>
        <BgTransformer src={bgUrl} id={id} fallbackTransformId={fallbackTransformId} />
        {/* The @2x canvas draws the mosaic at twice the size, so that both
            canvases show the same tiles. */}
        <PixelationLayer scale={scale} />

        <div className="content">
          <div className="product-name">
            <ContentEditable id={id} text={productName} />
          </div>
          <div className="subtitle">
            <Textarea id={id} text={version} />
          </div>
          <div className="splash-footer">
            <div className="powered-with">
              <div className="subtitle2">
                <Textarea id={id} text={poweredWith} inline />
              </div>
              <div className="badge">
                <Textarea id={id} text={airMark} inline normalize={toModulCase} />
              </div>
            </div>
            <header className="template-header">
              <div className="logo">
                <img src={logoUrl} alt="logo" />
              </div>
            </header>
          </div>
        </div>

        {[5, 6].includes(Number(layoutOption)) && (
          <div className="upload-wrap">
            <ImageUpload id={id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SplashLayout2026;
